/**
 * DashboardAnchors — sticky chip strip + collapsible-section primitives.
 *
 * Phase A.1 replacement for the dropped 3-column reorg. Goal: "see more,
 * scroll less" without restructuring the 3000-line DashboardLayout.
 *
 * Exports <AnchorStrip /> — the fixed rail rendered near the top of the page;
 * chips scroll the named section into view.
 *
 * It USED to also export a CollapsibleSection, a second component with the same
 * name as components/dashboard/CollapsibleSection.tsx (the real one, which
 * DashboardLayout imports) but a DIFFERENT storage prefix — 'dash.collapse.'
 * here vs 'dash-collapse-' there. Nothing imported it, so anyone who edited this
 * copy found their change had no effect. Deleted 2026-09-02; see
 * docs/CHARTS-FUNCTIONAL-ISSUES.md item 9.
 *
 * Each anchor target lives in a Box with `data-dash-anchor="<id>"`; the strip
 * uses scrollIntoView on click. No restructure of existing JSX needed.
 */
import React, { useEffect, useState } from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { useAppTheme } from '../../hooks/useAppTheme';
import { CH_TYPE } from '../../styles/appUi';

export interface AnchorDef {
  id: string;
  label: string;
  badge?: number; // optional small count badge
  /** Rail label. Without this the rail derived one by taking the FIRST WORD of
   *  `label`, which made "Manual Entry" and "Manual Trigger" both render as
   *  "Manual" — two buttons, identical text, different destinations. */
  short?: string;
}

/** Total width the fixed rail occupies, including its 6px left offset. The page
 *  reserves exactly this much so the rail never overlaps content. */
export const RAIL_GUTTER_PX = 100;

/**
 * Smooth-scroll the element tagged `[data-dash-anchor="id"]` into view.
 *
 * The app's outer Layout uses a Box with `overflowY: auto` as the scroll
 * container — NOT the window. Our prior implementation used
 * window.scrollTo which silently did nothing because window scrollY is
 * always 0 in that setup.
 *
 * scrollIntoView walks up the DOM to the nearest scrollable ancestor and
 * scrolls THAT — exactly what we want. The `scrollMarginTop` on each
 * anchor element handles the AppBar + sticky-strip offset declaratively.
 */
export const scrollToAnchor = (id: string) => {
  const el = document.querySelector(`[data-dash-anchor="${id}"]`) as HTMLElement | null;
  if (!el) return;
  try {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } catch {
    // Older browsers may reject options object — fall back to plain call
    el.scrollIntoView();
  }
};

interface AnchorStripProps {
  anchors: AnchorDef[];
  /**
   * R78: optional content rendered inside the same fixed rail, below the
   * Jump chips with a thin divider. Used for the Market Status badges so
   * they no longer consume horizontal flow on the Dashboard page.
   */
  footer?: React.ReactNode;
}

/**
 * Vertical "Jump To" rail — fixed-position strip on the LEFT edge of the
 * viewport, always visible regardless of page scroll.
 *
 * It takes no space in the document flow, which is why it used to DRAW ON TOP OF
 * THE PAGE at laptop widths: at 1366px it covered the first ~90px of every card,
 * so "RISK COCKPIT" read "encil to set cap" and "AI Desk" read "l Desk". The
 * page now reserves `RAIL_GUTTER_PX` on its left at the same breakpoint the rail
 * appears at (see DashboardLayout), so the rail can float without ever
 * overlapping anything. Export the width so the two can never drift apart.
 *
 * Each chip is short-labelled (first word) with the full label + badge
 * in a tooltip, so the rail stays narrow. Anchors whose target is not
 * currently rendered (empty section / hidden) render disabled so the
 * rail itself never claims to navigate somewhere that doesn't exist.
 */
export const AnchorStrip: React.FC<AnchorStripProps> = ({ anchors, footer }) => {
  const { C, isDark } = useAppTheme();
  // Re-render once on mount to pick up async-rendered anchor targets.
  const [tick, force] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => force(n => n + 1), 50);
    return () => clearTimeout(t);
  }, []);

  if (anchors.length === 0 && !footer) return null;

  // Use `tick` to silence the lint warning + ensure visibility re-evaluates
  void tick;
  const isVisibleInDom = (id: string) => document.querySelector(`[data-dash-anchor="${id}"]`) != null;

  /* Rail label: the anchor's own `short` when it has one, else the LAST word —
     which is the distinguishing one in this page's names ("Manual Entry" →
     "Entry", "Manual Trigger" → "Trigger", "AutoTrade Watch" → "Watch"), where
     the first word is the shared prefix. Anything that still collides sets
     `short` explicitly at the call site. Badge appears as a suffix when > 0. */
  const shortLabel = (a: AnchorDef) => {
    if (a.short) return a.short;
    const parts = a.label.split(/\s+/);
    return parts.length > 1 ? parts[parts.length - 1] : parts[0];
  };

  return (
    <Box
      role="navigation"
      aria-label="Dashboard section navigation"
      sx={{
        position: 'fixed',
        left: 6,
        top: { xs: 64, md: 96 }, // clear the AppBar (sticky stats strip)
        zIndex: 5,
        display: { xs: 'none', md: 'flex' }, // hide on mobile — too crowded
        flexDirection: 'column',
        gap: 0.4,
        px: 0.5, py: 0.75,
        borderRadius: 1.25,
        bgcolor: isDark ? 'rgba(13,17,23,0.85)' : 'rgba(255,255,255,0.9)',
        border: '1px solid', borderColor: 'divider',
        boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.5)' : '0 2px 8px rgba(0,0,0,0.08)',
        backdropFilter: 'blur(6px)',
        width: RAIL_GUTTER_PX - 12,
        maxWidth: RAIL_GUTTER_PX - 12,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700, color: 'text.secondary',
          textTransform: 'uppercase', letterSpacing: 0.4,
          fontSize: CH_TYPE.micro, textAlign: 'center',
          lineHeight: 1, py: 0.25,
        }}
      >
        Jump
      </Typography>
      {anchors.map(a => {
        const exists = isVisibleInDom(a.id);
        const badge = (a.badge != null && a.badge > 0) ? ` ${a.badge}` : '';
        return (
          <Tooltip
            key={a.id}
            arrow placement="right"
            title={exists ? `Scroll to ${a.label}${badge}` : `${a.label} not currently visible`}
          >
            <span>
              <Chip
                size="small"
                label={`${shortLabel(a)}${badge}`}
                onClick={() => scrollToAnchor(a.id)}
                disabled={!exists}
                sx={{
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: CH_TYPE.micro,
                  height: 21,
                  width: '100%',
                  borderRadius: 0.75,
                  bgcolor: exists ? C.neutralMuted : 'transparent',
                  color: exists ? C.neutral : 'text.disabled',
                  border: '1px solid',
                  borderColor: exists ? C.neutralBorder : 'divider',
                  '& .MuiChip-label': { px: 0.5 },
                  '&:hover': { bgcolor: exists ? C.neutral : 'transparent', color: exists ? C.onSolid : 'text.disabled' },
                  '&.Mui-disabled': { opacity: 0.4 },
                }}
              />
            </span>
          </Tooltip>
        );
      })}
      {footer && (
        <>
          <Box sx={{ height: '1px', bgcolor: 'divider', my: 0.5, mx: 0.25 }} />
          {footer}
        </>
      )}
    </Box>
  );
};
